<template>
  <button 
    class="boost-button" 
    @click="activateBoost"
    :class="{ active: isBoostActive, disabled: isOnCooldown }"
    :disabled="isBoostActive || isOnCooldown"
  >
    <div class="boost-content">
      <img 
        src="../assets/raketa.png" 
        alt="Boost" 
        class="boost-icon"
      />
      <span v-if="isOnCooldown" class="boost-text cooldown">{{ formatTime(cooldownTime) }}</span>
    </div>
  </button>
</template>

<script>
import { computed, onMounted, onUnmounted } from 'vue';

export default {
  name: 'BoostButton',
  props: {
    isBoostActive: {
      type: Boolean,
      required: true
    },
    boostExpireAt: {
      type: Number,
      default: null
    },
    boostCooldownEnd: {
      type: Number,
      default: null
    },
    balance: {
      type: Number,
      default: 0
    }
  },
  emits: ['activate-boost'],
  setup(props, { emit }) {
    let timer = null;
    
    const cooldownTime = computed(() => {
      if (!props.boostCooldownEnd) return 0;
      return Math.max(0, props.boostCooldownEnd - Date.now());
    });
    
    const isOnCooldown = computed(() => {
      if (!props.boostCooldownEnd) return false;
      return Date.now() < props.boostCooldownEnd;
    });
    
    const activateBoost = () => {
      // Check if boost can be activated
      if (props.isBoostActive) {
        // Show notification that boost is already active
        const remainingTime = props.boostExpireAt ? Math.max(0, props.boostExpireAt - Date.now()) : 0;
        if (remainingTime > 0) {
          const minutes = Math.floor(remainingTime / 60000);
          const seconds = Math.floor((remainingTime % 60000) / 1000);
          const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
          
          if (window.NotificationService && window.NotificationService.show) {
            window.NotificationService.show(
              "Уже активировано", 
              `Подождите ${timeString} для обратной активации`, 
              'info'
            );
          }
        }
      } else if (isOnCooldown.value) {
        // Show notification about cooldown
        const timeString = formatTime(cooldownTime.value);
        
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show(
            "Уже активировано", 
            `Подождите ${timeString} для обратной активации`, 
            'info'
          );
        }
      } else {
        emit('activate-boost');
      }
    };
    
    const formatTime = (milliseconds) => {
      if (milliseconds <= 0) return '00:00';
      
      const totalSeconds = Math.ceil(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };
    
    onMounted(() => {
      // Update timer every second
      timer = setInterval(() => {
        // This will trigger reactivity
      }, 1000);
    });
    
    onUnmounted(() => {
      if (timer) clearInterval(timer);
    });
    
    return {
      cooldownTime,
      isOnCooldown,
      activateBoost,
      formatTime
    };
  }
};
</script>

<style scoped>
.boost-button {
  background: transparent;
  border: none;
  color: #a0a0a0;
  font-weight: 600;
  cursor: pointer;
  transform-origin: center;
  animation: float 3s ease-in-out infinite;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 10px;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: none;
  min-width: 120px;
  height: 55px; /* Увеличена высота под большую иконку */
  display: flex;
  align-items: center;
  justify-content: center;
}

.boost-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.boost-icon {
  width: 55px; /* Увеличенный размер до 55px (40px + 15px) */
  height: 55px;
  object-fit: contain;
  position: relative;
  top: -2px; /* Поднят чуть вверх */
}

.boost-text {
  font-size: 14px;
  font-weight: 600;
}

.boost-text.cooldown {
  color: #ff9800;
}


.boost-button:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #ccc;
}

.boost-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #151515;
  border-color: #252525;
  color: #666;
}

.boost-button.active {
  color: #4CAF50;
  text-shadow: 0 0 15px rgba(76, 175, 80, 0.6);
  animation: pulse 1s infinite;
  background: transparent;
  border-color: transparent;
  padding: 8px 16px;
}

.boost-button.disabled {
  color: #ff9800;
  text-shadow: 0 0 10px rgba(255, 152, 0, 0.5);
  background: transparent;
  border-color: transparent;
  padding: 8px 16px;
}

@keyframes float {
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(4px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}
</style>